import { addMinutes, isPast, isToday } from 'date-fns';
import { NotificationFeedbackType } from 'expo-haptics';
import type { AddressType } from '@sweetgreen/garnish';
import {
  getRoundedValueOfPercentage,
  triggerHapticNotificationFeedback,
} from '@sweetgreen/garnish';

import type {
  AvailableWantedTime,
  CardType,
  DeliveryOrderDetailInput,
  EstimateRange,
  Ledger,
} from '@order/graphql';
import type { useCart } from '@order/hooks';
import type { TranslationKeys, TranslationValues } from '@order/Localization';
import type { useLocalizationContext } from '@order/Localization';
import type { WeekdayTranslationKey } from '@order/utils';
import { getDay, ignoreTimezone } from '@order/utils';

export const isTimeAvailable = ({ disabled }: WantedTimeOption) => !disabled;

export const mapAvailableTimes = ({
  t,
  orderType,
  times,
  deliveryEstimate,
}: WantedTimeMessage): WantedTimeOptions => {
  if (!times || !orderType) return {};

  // ───────────────────────────────────────────────────────────────

  let hasValidAsap = false;

  return times.reduce<WantedTimeOptions>((groups, { time: value }) => {
    const availableDate = ignoreTimezone(value);

    if (!availableDate) return groups;

    const { day, clock, formattedHours, formattedMinutes } =
      formatTimeParts(availableDate);

    // 10 minutes delivery window
    const deliveryWindowEnd = addMinutes(availableDate, 10);
    const {
      day: endDay,
      clock: endClock,
      formattedHours: formattedHoursEnd,
      formattedMinutes: formattedMinutesEnd,
    } = formatTimeParts(deliveryWindowEnd);

    // ────────────────────────────────────────────────────────────────────

    const hasPast = isPast(new Date(value));
    const isAsap = !hasValidAsap && !hasPast;

    // Displays as "ASAP - 35-45 MINS".
    const isAsapWithDeliveryEstimate = isAsap && deliveryEstimate;

    if (isAsap) hasValidAsap = true;
    const isAvailableDateToday = isToday(availableDate);

    // ────────────────────────────────────────────────────────────────────

    const labelTranslationKey = generateTimeLabelKey(day, isAvailableDateToday);
    const asapLabel = isAsap ? `${t('general.asap')} - ` : '';
    const asapDeliveryLabel = isAsapWithDeliveryEstimate
      ? t('general.asap-delivery', deliveryEstimate)
      : '';
    const groupLabel = isAsapWithDeliveryEstimate
      ? asapDeliveryLabel
      : `${asapLabel}${t(labelTranslationKey, { day })}`;
    const groupOptions = groups[groupLabel] ?? [];

    // ────────────────────────────────────────────────────────────────────

    const pickupTimeDetails = t('general.available-time', {
      day: t(day),
      hours: formattedHours,
      minutes: formattedMinutes,
      clock,
    });

    const deliveryStartTimeDetails = t('general.available-start-time', {
      day: t(day),
      hours: formattedHours,
      minutes: formattedMinutes,
    });

    const deliveryEndTimeDetails = t('general.available-time', {
      day: t(endDay),
      hours: formattedHoursEnd,
      minutes: formattedMinutesEnd,
      clock: endClock,
    });

    const timeDetailsWithPickupLabel = `${pickupTimeDetails}`;
    const timeDetailsWithDeliveryLabel = isAsapWithDeliveryEstimate
      ? ''
      : `${deliveryStartTimeDetails}-${deliveryEndTimeDetails}`;

    // ────────────────────────────────────────────────────────────────────

    const timeOption = {
      value,
      disabled: hasPast,
      label:
        orderType === 'delivery'
          ? timeDetailsWithDeliveryLabel
          : timeDetailsWithPickupLabel,
    };

    return {
      ...groups,
      [groupLabel]: [...groupOptions, timeOption],
    };
  }, {});
};

export const getWantedTimeFormatting = (
  value: string,
  t: ReturnType<typeof useLocalizationContext>['t'],
) => {
  const availableDate = ignoreTimezone(value);

  if (!availableDate) return;

  const { clock, formattedHours, formattedMinutes } =
    formatTimeParts(availableDate);

  return t('general.available-time', {
    hours: formattedHours,
    minutes: formattedMinutes,
    clock,
  });
};

const formatTimeParts = (date: Date) => {
  const day = getDay(`${date.getDay()}`);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const clock = hours >= 12 ? 'pm' : 'am';
  const formattedHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  const formattedMinutes = String(minutes).padStart(2, '0');

  return {
    day,
    clock,
    formattedHours,
    formattedMinutes,
  };
};

const generateTimeLabelKey = (
  day: WeekdayTranslationKey,
  isAvailableTimeToday: boolean,
) => (isAvailableTimeToday ? 'general.available-time-today' : day);

export const applyCredit = (
  ledger: Partial<Ledger> | undefined,
  creditAmount: number,
): Partial<Ledger> | undefined => {
  const credits = ledger?.credits?.map((credit) =>
    credit.name === 'Credit' ? { ...credit, amount: creditAmount } : credit,
  );

  return (
    ledger && {
      ...ledger,
      credits,
      creditsTotal: credits?.reduce((prev, credit) => prev + credit.amount, 0),
    }
  );
};

const applyDeliveryTip = (
  ledger: NonNullable<Cart>['ledger'],
  payloadDeliveryOrderDetail: CheckoutPayload['deliveryOrderDetail'],
): Ledger | undefined => {
  const deliveryTip = payloadDeliveryOrderDetail?.tip;
  const subtotal = ledger.subtotal ?? 0;
  const defaultDeliveryTip = getRoundedValueOfPercentage(
    DEFAULT_DELIVERY_TIP_PERCENTAGE,
    subtotal,
  );

  const tip = deliveryTip ?? defaultDeliveryTip;

  return { ...ledger, tip };
};

const applyPickupTip = (
  ledger: NonNullable<Cart>['ledger'],
  payloadTipAmount: number | undefined,
): Ledger | undefined => {
  const subtotal = ledger.subtotal ?? 0;
  const defaultPickupTipAmount = getRoundedValueOfPercentage(
    DEFAULT_PICKUP_TIP_PERCENTAGE,
    subtotal,
  );

  const tip = payloadTipAmount ?? defaultPickupTipAmount;

  return { ...ledger, tip };
};

export function withTip(props: ApplyTipParams): Ledger | undefined {
  const { payload, shouldApplyPickupTip, cart } = props;

  const ledger = cart?.ledger;
  const orderType = cart?.orderType;

  if (!ledger) return;

  if (shouldApplyPickupTip && orderType === 'PICKUP') {
    return applyPickupTip(ledger, payload.tip);
  }

  if (orderType === 'DELIVERY') {
    return applyDeliveryTip(ledger, payload.deliveryOrderDetail);
  }

  return ledger;
}

// The default option for delivery tips is 15%.
const DEFAULT_DELIVERY_TIP_PERCENTAGE = 15;
const DEFAULT_PICKUP_TIP_PERCENTAGE = 0;

export const calculateTip = (
  subtotal: number | undefined,
  percentage?: number,
) => {
  return Math.round(
    ((percentage ?? DEFAULT_DELIVERY_TIP_PERCENTAGE) / 100) * (subtotal ?? 0),
  );
};

export const triggerCheckoutHapticFeedback = async (
  isCheckoutSuccess: boolean,
) => {
  const feedbackType = isCheckoutSuccess
    ? NotificationFeedbackType.Success
    : NotificationFeedbackType.Warning;

  await triggerHapticNotificationFeedback(feedbackType);
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type CheckoutPayload = {
  readonly wantedTime: string;
  readonly billingAccountId: string;
  readonly useCredit: boolean;
  readonly dropoffLocationId: string | null;
  readonly contactNumber: string | null;
  readonly includeUtensils: boolean;
  readonly deliveryOrderDetail?: DeliveryOrderDetailInput | null;
  readonly tip?: number; // restaurant tip
};

type WantedTimeOptions = Record<string, readonly WantedTimeOption[]>;

type WantedTimeOption = {
  readonly value: string;
  readonly label: string;
  readonly disabled: boolean;
};

type WantedTimeMessage = {
  readonly t: (key: TranslationKeys, values?: TranslationValues) => string;
  readonly orderType?: AddressType;
  readonly times?: readonly AvailableWantedTime[];
  readonly deliveryEstimate?: EstimateRange | null;
};

export type CreditCardOption = {
  readonly value: string;
  readonly label: string;
  readonly cardType: CardType | 'NEW';
};

export type SelectedTimeProps = {
  readonly selectedTime: string;
  readonly setSelectedTime: (time: string) => void;
};

type ApplyTipParams = {
  readonly cart: Cart;
  readonly payload: CheckoutPayload;
  readonly shouldApplyPickupTip: boolean;
};

type Cart = ReturnType<typeof useCart>['cart'];
